'use client';

import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { Sort } from '@/models/CategoryDetail';
import { Button } from './ui/button';
import { Filter, FilterOption } from '@/models/Filters';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Input } from './ui/input';
import { useUrunlerListingNavigationOptional } from '@/contexts/UrunlerListingNavigationContext';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { flushSync } from 'react-dom';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Sort as SortIcon } from '@/icons/Sort';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Drawer, DrawerContent, DrawerTrigger } from './ui/drawer';
import { Spinner } from './ui/spinner';

export default function SortAndFilterOptionsContent({
  sortOptions,
  activeSort,
  filterOptions = [],
  activeFilters,
}: {
  sortOptions?: Sort[];
  activeSort?: string;
  filterOptions?: Filter[];
  activeFilters?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const listingNav = useUrunlerListingNavigationOptional();
  const [, startTransition] = useTransition();
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: number]: FilterOption[];
  }>({});
  const [searchQuery, setSearchQuery] = useState<{ [key: string]: string }>({});
  const [appliedFilters, setAppliedFilters] = useState<{
    [key: number]: FilterOption[];
  }>({});
  const [loadingContext, setLoadingContext] = useState<'filters' | 'sort' | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingStartRef = useRef<number | null>(null);
  const [openFilterDropdownId, setOpenFilterDropdownId] = useState<number | null>(null);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const [filterDrawerActiveFilter, setFilterDrawerActiveFilter] = useState<Filter | undefined>(
    undefined,
  );
  const [isSortDrawerVisible, setIsSortDrawerVisible] = useState(false);
  const [selectedSort, setSelectedSort] = useState<string | undefined>(activeSort);

  useEffect(() => {
    if (!activeFilters) {
      setAppliedFilters({});
      setSelectedFilters({});
      return;
    }

    const filterGroups = activeFilters.split(';');
    const newAppliedFilters: { [key: number]: FilterOption[] } = {};

    filterGroups.forEach((group, index) => {
      const options = group.split(',');
      const filter = filterOptions.find((filterOption) =>
        filterOption.options.some((option) => options.includes(option.id.toString())),
      );
      if (filter) {
        newAppliedFilters[filter.id] = filter.options.filter((option) =>
          options.includes(option.id.toString()),
        );
      }
    });
    setAppliedFilters(newAppliedFilters);
    setSelectedFilters(newAppliedFilters);
  }, [activeFilters, filterOptions]);

  useEffect(() => {
    if (!loadingContext) return;

    const minVisibleMs = 300;
    const startedAt = loadingStartRef.current ?? Date.now();
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, minVisibleMs - elapsed);

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    loadingTimeoutRef.current = setTimeout(() => {
      setLoadingContext(null);
      loadingTimeoutRef.current = null;
      loadingStartRef.current = null;
    }, remaining);
  }, [searchParams, loadingContext]);

  // Spinner'ı URL değişiminde değil; yeni liste render edilince kapat.
  useEffect(() => {
    if (!listingNav) return;
    if (!listingNav.pending) {
      setLoadingContext(null);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      loadingStartRef.current = null;
    }
  }, [listingNav?.pending]);

  useEffect(
    () => () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    },
    [],
  );

  const startLoading = (context: 'filters' | 'sort') => {
    setLoadingContext(context);
    loadingStartRef.current = Date.now();
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    loadingTimeoutRef.current = setTimeout(() => {
      setLoadingContext(null);
      loadingTimeoutRef.current = null;
      loadingStartRef.current = null;
    }, 4000);
  };

  const buildUrl = (params: { [key: string]: string | undefined }) => {
    const newParams = new URLSearchParams(searchParams.toString());

    // Reset pagination when changing sort/filters to avoid generating many distinct URLs.
    if ('sort' in params || 'filters' in params) {
      newParams.delete('sayfa');
    }

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });

    const qs = newParams.toString();
    return `${pathname}${qs ? `?${qs}` : ''}`;
  };

  const pushListingUrl = useCallback(
    (url: string) => {
      // Pending state must paint before router navigation; otherwise one frame can show old products under skeleton.
      flushSync(() => {
        listingNav?.beginListingNavigation();
      });
      startTransition(() => {
        router.push(url);
      });
    },
    [listingNav, router, startTransition],
  );

  const handleCheckboxChange = (filterId: number, option: FilterOption) => {
    setSelectedFilters((prev) => {
      const current = prev[filterId] || [];
      const updated = current.includes(option)
        ? current.filter((id) => id !== option)
        : [...current, option];

      return {
        ...prev,
        [filterId]: updated,
      };
    });
  };

  const handleApplyFilters = () => {
    startLoading('filters');
    const groups: string[] = [];
    const nextAppliedFilters: { [key: number]: FilterOption[] } = {};

    Object.values(selectedFilters).forEach((options) => {
      if (options.length > 0) {
        const ids = options
          .map((o) => o.id)
          .sort((a, b) => a - b)
          .join(',');
        groups.push(ids);
      }
    });

    Object.entries(selectedFilters).forEach(([key, options]) => {
      if (options.length > 0) {
        nextAppliedFilters[Number(key)] = options;
      }
    });
    setAppliedFilters(nextAppliedFilters);

    // Sort groups as well for stable, canonical URLs.
    groups.sort();

    if (groups.length > 0) {
      pushListingUrl(buildUrl({ filters: groups.join(';') }));
    } else {
      pushListingUrl(buildUrl({ filters: undefined }));
    }
  };

  const handleApplySort = (value: string) => {
    startLoading('sort');
    pushListingUrl(
      buildUrl({
        sort: value,
      }),
    );
    setIsSortDropdownOpen(false);
  };

  const handleClearFilters = (filterId: number) => {
    if (filterId < 0) {
      setSelectedFilters({});
      setAppliedFilters({});
      setSearchQuery({});
      pushListingUrl(buildUrl({ filters: undefined }));
      return;
    }

    setSelectedFilters((prev) => ({ ...prev, [filterId]: [] }));
    setAppliedFilters((prev) => {
      const next = { ...prev };
      delete next[filterId];
      return next;
    });
    setSearchQuery((prev) => ({ ...prev, [filterId]: '' }));

    const newFilters: string[] = [];

    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value.length > 0 && key != filterId.toString()) {
        newFilters.push(value.map((option) => option.id).join(','));
      }
    });

    const newParams = new URLSearchParams(searchParams.toString());

    if (newFilters.length > 0) {
      newParams.set('filters', newFilters.join(';'));
    } else {
      newParams.delete('filters');
    }

    pushListingUrl(`${pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`);
  };

  const handleClearSort = () => {
    pushListingUrl(buildUrl({ sort: undefined }));
  };

  const handleRemoveFilter = (option: FilterOption) => {
    const newParams = new URLSearchParams(searchParams.toString());
    const currentFilters = newParams.get('filters')?.split(';') || [];

    const updatedFilters = currentFilters
      .map((group) => {
        const options = group.split(',');
        if (options.includes(option.id.toString())) {
          const newOptions = options.filter((id) => id !== option.id.toString());
          return newOptions.length > 0 ? newOptions.join(',') : null;
        }
        return group;
      })
      .filter(Boolean);

    if (updatedFilters.length > 0) {
      newParams.set('filters', updatedFilters.join(';'));
    } else {
      newParams.delete('filters');
    }

    const newUrl = `${pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`;
    pushListingUrl(newUrl);
  };

  return (
    <div className="w-full flex flex-col gap-4 px-4 sm:px-0 relative">
      {loadingContext && (
        <div className="pointer-events-none absolute left-0 right-0 top-0 z-50 flex justify-center">
          <div className="mt-16 inline-flex items-center gap-2 rounded-full bg-[#2E2E2C]/90 px-4 py-2 text-[12px] text-white shadow-md">
            <Spinner size={14} className="text-white" />
            {loadingContext === 'filters' ? 'Filtreler güncelleniyor' : 'Sıralama güncelleniyor'}
          </div>
        </div>
      )}
      <div className="w-full grid grid-cols-2 gap-2 sm:flex sm:gap-4 items-center justify-between">
        {/* Filter Dropdown */}
        {filterOptions.length > 0 && (
          <div className="hidden sm:flex items-center gap-2">
            {filterOptions.map((filter) => (
              <DropdownMenu
                key={filter.id}
                open={openFilterDropdownId === filter.id}
                onOpenChange={(open) => setOpenFilterDropdownId(open ? filter.id : null)}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    className={`h-[38px] border bg-white hover:bg-white rounded-full px-4 gap-2 focus-visible:ring-transparent focus-visible:ring-offset-transparent ${
                      appliedFilters[filter.id]?.length > 0
                        ? 'border-[#D2F25C]'
                        : 'border-[#EDEDF0]'
                    }`}
                  >
                    <p className="text-[#2E2E2C] font-medium text-[12px]">{filter.title}</p>
                    {loadingContext === 'filters' && (
                      <Spinner size={14} className="text-[#2E2E2C]" />
                    )}
                    {appliedFilters[filter.id]?.length > 0 && (
                      <span className="flex items-center justify-center bg-[#D2F25C] text-[#2B3280] text-[11px] rounded-full px-[10px] py-[0px]">
                        {appliedFilters[filter.id]?.length}
                      </span>
                    )}
                    <ChevronDown className="h-4 w-4 text-[#2E2E2C]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[280px] px-0 py-2 rounded-[12px]">
                  <div className="p-2">
                    <div className="relative">
                      <Input
                        placeholder="Ara..."
                        className="pl-4 h-9 bg-[#F5F5F0] border-none text-[13px] rounded-full placeholder:text-[#5C5C57]"
                        value={searchQuery[filter.id] || ''}
                        onChange={(e) =>
                          setSearchQuery((prev) => ({
                            ...prev,
                            [filter.id]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <ScrollArea className="h-[200px]">
                    <div className="px-2 py-2">
                      {filter.options
                        .filter(
                          (option) =>
                            !searchQuery[filter.id] ||
                            option.label
                              .toLowerCase()
                              .includes(searchQuery[filter.id]?.toLowerCase() || ''),
                        )
                        .map((option) => (
                          <div
                            key={option.id}
                            className="px-2 flex items-center gap-2 h-[32px] hover:bg-[#F5F5F0] cursor-pointer rounded-md"
                            onClick={() => handleCheckboxChange(filter.id, option)}
                          >
                            <Checkbox
                              checked={selectedFilters[filter.id]?.includes(option)}
                              // onCheckedChange={() =>
                              //   handleCheckboxChange(filter.id, option)
                              // }
                              className="data-[state=checked]:bg-[#5896F5] data-[state=checked]:border-none border-[#F5F5F0] border-2"
                            />
                            <p className="text-[#2E2E2C] font-normal text-[12px]">{option.label}</p>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                  <DropdownMenuSeparator />
                  <div className="p-2 flex flex-col gap-2 bg-white">
                    <Button
                      onClick={() => {
                        handleApplyFilters();
                        setOpenFilterDropdownId(null);
                      }}
                      disabled={loadingContext === 'filters'}
                      className="flex-1 h-[28px] bg-hzc_purple_500 hover:bg-hzc_purple_500 text-[13px] text-[#FAFAF8] font-medium rounded-full"
                    >
                      {loadingContext === 'filters' ? (
                        <span className="inline-flex items-center gap-2">
                          <Spinner size={14} className="text-[#FAFAF8]" />
                          Uygulanıyor
                        </span>
                      ) : (
                        'Uygula'
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        handleClearFilters(filter.id);
                        setOpenFilterDropdownId(null);
                      }}
                      className="flex-1 h-[28px] bg-transparent hover:bg-[#F5F5F0] text-[13px] text-[#5C5C57] font-medium underline rounded-full"
                    >
                      Temizle
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
          </div>
        )}

        {/* Filter Drawer */}
        {filterOptions.length > 0 && (
          <Drawer open={isFilterDrawerVisible} onOpenChange={setIsFilterDrawerVisible}>
            <DrawerTrigger asChild>
              <Button
                className={`sm:hidden h-[38px] w-full border border-[#EDEDF0] bg-white hover:bg-white rounded-full px-4 focus-visible:ring-transparent focus-visible:ring-offset-transparent ${
                  Object.keys(appliedFilters).length > 0 ? 'border-[#D2F25C]' : 'border-[#EDEDF0]'
                }`}
              >
                <SortIcon className="h-[20px] w-[18px] text-[#2E2E2C] text-center" />
                <p className="text-[#2E2E2C] font-medium text-[12px] ml-1 mr-2">Filtrele</p>
                {loadingContext === 'filters' && <Spinner size={14} className="text-[#2E2E2C]" />}
                {Object.keys(appliedFilters).length > 0 && (
                  <span className="flex items-center justify-center bg-[#D2F25C] text-[#2B3280] text-[11px] rounded-full px-[10px] py-[0px]">
                    {Object.keys(appliedFilters).length}
                  </span>
                )}
                <ChevronDown className="h-4 w-4 ml-2 text-[#2E2E2C]" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="bg-[#2B3280] border-none">
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-center border-b-2 border-[#4D5394] pb-2 relative h-12">
                  {filterDrawerActiveFilter ? (
                    <Button
                      className="text-white hover:bg-transparent absolute left-0 bg-transparent"
                      onClick={() => setFilterDrawerActiveFilter(undefined)}
                    >
                      <ArrowLeft className="text-white size-6" />
                    </Button>
                  ) : (
                    <Button
                      className="text-white hover:bg-transparent absolute left-0 text-[12px] font-medium underline h-[36px] bg-transparent"
                      onClick={() => handleClearFilters(-1)}
                    >
                      Temizle
                    </Button>
                  )}
                  <span className="text-[16px] w-fit text-white font-medium">
                    {filterDrawerActiveFilter?.title || 'Filtreleme'}
                  </span>
                  {!filterDrawerActiveFilter && (
                    <Button
                      className="text-white hover:bg-transparent absolute right-0 bg-transparent"
                      onClick={() => setIsFilterDrawerVisible(false)}
                    >
                      <X className="text-white size-6" />
                    </Button>
                  )}
                </div>
                {filterDrawerActiveFilter ? (
                  <>
                    <div className="p-2">
                      <div className="relative">
                        <Input
                          placeholder="Ara..."
                          className="pl-4 h-9 bg-[#F5F5F0] border-none text-[13px] rounded-full placeholder:text-[#5C5C57]"
                          value={searchQuery[filterDrawerActiveFilter.id] || ''}
                          onChange={(e) =>
                            setSearchQuery((prev) => ({
                              ...prev,
                              [filterDrawerActiveFilter.id]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <ScrollArea className="h-[60vh] p-2">
                      <div className="px-2">
                        {filterDrawerActiveFilter.options
                          .filter(
                            (option) =>
                              !searchQuery[filterDrawerActiveFilter.id] ||
                              option.label
                                .toLowerCase()
                                .includes(
                                  searchQuery[filterDrawerActiveFilter.id]?.toLowerCase() || '',
                                ),
                          )
                          .map((option) => (
                            <div
                              key={option.id}
                              className="flex items-center gap-2 h-[36px] cursor-pointer rounded-md "
                              onClick={() =>
                                handleCheckboxChange(filterDrawerActiveFilter.id, option)
                              }
                            >
                              <Checkbox
                                checked={selectedFilters[filterDrawerActiveFilter.id]?.includes(
                                  option,
                                )}
                                // onCheckedChange={() =>
                                //   handleCheckboxChange(
                                //     filterDrawerActiveFilter.id,
                                //     option
                                //   )
                                // }
                                className="data-[state=checked]:bg-[#5896F5] data-[state=checked]:border-none border-[#F5F5F0] border-2"
                              />
                              <p className="text-white font-normal text-[12px]">{option.label}</p>
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </>
                ) : (
                  <ScrollArea className="h-[60vh] p-2 px-4">
                    {Object.entries(selectedFilters).some(([_, options]) => options.length > 0) && (
                      <div className="flex flex-col gap-2">
                        <span className="text-[15px] font-normal text-white">Seçili Filtreler</span>
                        <div className="flex flex-wrap gap-2 border-b border-[#4D5394] pb-4">
                          {Object.entries(selectedFilters).map(([filterId, options]) =>
                            options.map((option) => (
                              <div
                                key={option.id}
                                className="flex items-center gap-1.5 bg-[#ECEAE1] px-3 py-1.5 rounded-full"
                              >
                                <span className="text-[12px] text-[#2E2E2C]">{option.label}</span>
                                <button
                                  onClick={() => handleCheckboxChange(Number(filterId), option)}
                                  className="text-[#5C5C57] hover:text-[#2E2E2C]"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            )),
                          )}
                        </div>
                      </div>
                    )}
                    {filterOptions.map((filterOption) => (
                      <div
                        key={filterOption.id}
                        onClick={() => setFilterDrawerActiveFilter(filterOption)}
                        className="flex flex-row items-center gap-2 py-3 text-[14px] font-normal text-white w-full border-b border-[#4D5394]"
                      >
                        {filterOption.title}
                        {selectedFilters[filterOption.id]?.length > 0 && (
                          <span className="flex items-center justify-center bg-[#D2F25C] text-[#2B3280] text-[11px] rounded-full px-[10px] py-[0px]">
                            {selectedFilters[filterOption.id]?.length}
                          </span>
                        )}
                        <ChevronRight className="size-4 absolute right-4" />
                      </div>
                    ))}
                  </ScrollArea>
                )}

                <Button
                  className="bg-[#D2F25C] rounded-[12px] h-[50px] text-center text-[16px] font-semibold hover:bg-[#D2F25C] text-[#2B3280] duration-300 gap-2 mx-4 mb-4"
                  onClick={() => {
                    if (!filterDrawerActiveFilter) {
                      handleApplyFilters();
                      setIsFilterDrawerVisible(false);
                    } else {
                      setAppliedFilters(selectedFilters);
                      handleApplyFilters();
                      setFilterDrawerActiveFilter(undefined);
                      setIsFilterDrawerVisible(false);
                    }
                  }}
                  disabled={loadingContext === 'filters'}
                >
                  {loadingContext === 'filters' ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner size={16} className="text-[#2B3280]" />
                      Yükleniyor
                    </span>
                  ) : filterDrawerActiveFilter ? (
                    'Uygula'
                  ) : (
                    'Sonuçları Gör'
                  )}
                </Button>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {/* Sort Dropdown */}
        {sortOptions && sortOptions.length > 0 && (
          <DropdownMenu open={isSortDropdownOpen} onOpenChange={setIsSortDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button className="hidden sm:flex h-[38px] w-full sm:w-fit border border-[#EDEDF0] bg-white hover:bg-white rounded-full px-4 focus-visible:ring-transparent focus-visible:ring-offset-transparent">
                <SortIcon className="h-[20px] w-[18px] text-[#2E2E2C] text-center" />
                <p className="text-[#2E2E2C] font-medium text-[12px] ml-1 mr-2">Sırala</p>
                <p className="text-[#5C5C57] font-normal text-[12px]">
                  {activeSort
                    ? sortOptions.find(
                        (option) => option.value.toString() === activeSort.toString(),
                      )?.label
                    : sortOptions.find((option) => option.selected)?.label}
                </p>
                {loadingContext === 'sort' && <Spinner size={14} className="text-[#2E2E2C]" />}
                <ChevronDown className="h-4 w-4 ml-2 text-[#2E2E2C]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[196px] px-0 py-2 rounded-[12px]">
              <ScrollArea className="h-[200px]">
                <div className="px-2 py-2">
                  <RadioGroup defaultValue="comfortable">
                    {sortOptions.map((option) => (
                      <div
                        key={option.value}
                        className="px-2 flex items-center gap-2 h-[32px] hover:bg-[#F5F5F0] cursor-pointer rounded-md"
                        onClick={() => {
                          handleApplySort(option.value.toString());
                        }}
                      >
                        <RadioGroupItem
                          value={option.value.toString()}
                          id={option.value.toString()}
                          checked={activeSort === option.value.toString()}
                          onClick={() => {
                            handleApplySort(option.value.toString());
                          }}
                          className="size-[13px] border-[#E2E4E9] data-[state=checked]:bg-hzc_purple_500 data-[state=checked]:text-transparent data-[state=checked]:border-none border-2"
                        />
                        <p className="text-[#2E2E2C] font-normal text-[12px]">{option.label}</p>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Sort Drawer */}
        {sortOptions && sortOptions.length > 0 && (
          <Drawer open={isSortDrawerVisible} onOpenChange={setIsSortDrawerVisible}>
            <DrawerTrigger asChild>
              <Button
                className={`sm:hidden h-[38px] w-full border border-[#EDEDF0] bg-white hover:bg-white rounded-full px-4 focus-visible:ring-transparent focus-visible:ring-offset-transparent
                ${activeSort ? 'border-[#D2F25C]' : 'border-[#EDEDF0]'}
                  `}
              >
                <SortIcon className="h-[20px] w-[18px] text-[#2E2E2C] text-center" />
                <p className="text-[#2E2E2C] font-medium text-[12px] ml-1 mr-2">Sırala</p>
                {loadingContext === 'sort' && <Spinner size={14} className="text-[#2E2E2C]" />}
                <ChevronDown className="h-4 w-4 ml-2 text-[#2E2E2C]" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="bg-[#2B3280] border-none">
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-center border-b-2 border-[#4D5394] pb-2 relative h-12">
                  <Button
                    className="text-white hover:bg-transparent absolute left-0 text-[12px] font-medium underline h-[36px] bg-transparent"
                    onClick={() => handleClearSort()}
                  >
                    Temizle
                  </Button>
                  <span className="text-[16px] w-fit text-white font-medium">Sıralama</span>
                  <Button
                    className="text-white hover:bg-transparent absolute right-0 bg-transparent"
                    onClick={() => setIsSortDrawerVisible(false)}
                  >
                    <X className="text-white size-6" />
                  </Button>
                </div>
                <ScrollArea className="h-[60vh] p-2">
                  <RadioGroup
                    defaultValue="comfortable"
                    onValueChange={(value) => setSelectedSort(value)}
                  >
                    {sortOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center gap-2 h-[48px] hover:bg-transparent cursor-pointer rounded-md px-2"
                        onClick={() => setSelectedSort(option.value.toString())}
                      >
                        <RadioGroupItem
                          checked={selectedSort === option.value.toString()}
                          className="size-[13px] border-[#E2E4E9] data-[state=checked]:bg-white data-[state=checked]:text-[#E2E4E9] data-[state=checked]:border-none border-2"
                          value={option.value.toString()}
                        />
                        <p className="text-white font-normal text-[14px]">{option.label}</p>
                      </div>
                    ))}
                  </RadioGroup>
                </ScrollArea>
                <Button
                  className="bg-[#D2F25C] rounded-[12px] h-[50px] text-center text-[16px] font-semibold hover:bg-[#D2F25C] text-[#2B3280] duration-300 mx-4 mb-4"
                  onClick={() => {
                    startLoading('sort');
                    pushListingUrl(
                      buildUrl({
                        sort: selectedSort || undefined,
                      }),
                    );
                    setIsSortDrawerVisible(false);
                  }}
                  disabled={loadingContext === 'sort'}
                >
                  {loadingContext === 'sort' ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner size={16} className="text-[#2B3280]" />
                      Yükleniyor
                    </span>
                  ) : (
                    'Sonuçları Gör'
                  )}
                </Button>
              </div>
            </DrawerContent>
          </Drawer>
        )}
      </div>

      {/* Active Filters Display */}
      {Object.keys(appliedFilters).length > 0 && (
        <div className="hidden sm:flex flex-wrap gap-2">
          {Object.entries(appliedFilters).map(([_, options]) =>
            options.map((option) => (
              <div
                key={option.id}
                className="flex items-center gap-1.5 bg-[#ECEAE1] px-3 py-1.5 rounded-full"
              >
                <span className="text-[12px] text-[#2E2E2C]">{option.label}</span>
                <button
                  onClick={() => handleRemoveFilter(option)}
                  className="text-[#5C5C57] hover:text-[#2E2E2C]"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )),
          )}
          <Button
            onClick={() => {
              pushListingUrl(pathname);
            }}
            variant="link"
            className="text-[13px] text-[#5C5C57] hover:text-[#2E2E2C] p-0 h-auto font-medium underline ml-2"
          >
            Temizle
          </Button>
        </div>
      )}
    </div>
  );
}
